import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { taskService, Task, UpdateTaskData } from '../../services/taskService';
import { tagService, Tag } from '../../services/tagService';
import { projectService } from '../../services/projectService';
import { Project } from '../../types/Project';
import BlockService, { ContentBlock } from '../../services/blockService';
import { TimeLogsService, TimeLog, CreateTimeLogData } from '../../services/timelogsService';
import BlockEditor from '../notes/BlockEditor';
import { useBlockManager } from '../../hooks/useBlockManager';
import { BlockContainer } from '../../services/blockManager';
import TagSelector from './TagSelector';
import './TaskManagement.css';

interface TaskDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

// TaskNote is now just ContentBlock since we don't use relationships
type TaskNote = ContentBlock;

const TaskDetail: React.FC<TaskDetailProps> = ({ user, onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{[key: string]: string}>({});

  // Notes state
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  // Collapsible sections state
  const [showDescriptionSection, setShowDescriptionSection] = useState(false);
  const [showMetadataSection, setShowMetadataSection] = useState(false);
  const [showTimeTrackingSection, setShowTimeTrackingSection] = useState(false);
  const [showBlocksSection, setShowBlocksSection] = useState(false);
  const [showQuickActionsPopover, setShowQuickActionsPopover] = useState(false);
  
  // Tags state
  const [taskTags, setTaskTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  // Projects state
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Time tracking state
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [timeLogsLoading, setTimeLogsLoading] = useState(false);
  const [timeStats, setTimeStats] = useState<{
    totalMinutes: number;
    totalHours: number;
    sessionCount: number;
    avgDurationMinutes: number;
  }>({ totalMinutes: 0, totalHours: 0, sessionCount: 0, avgDurationMinutes: 0 });
  
  // Timer state
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState<string | null>(null);
  const [timerDuration, setTimerDuration] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Manual time entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    duration: '',
    description: '',
    startTime: '',
    endTime: ''
  });

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Block management with standardized hook
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loading: blocksLoading,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'task',
    containerId: '', // Start with empty, use setContainerId to update dynamically
    onContainerUpdate: async (container: BlockContainer) => {
      if (task) {
        await taskService.updateTask({
          id: task.id.toString(),
          blocks: container.blocks
        });
      }
    }
  });

  useEffect(() => {
    if (id && user?.id) {
      loadTask();
      loadTimeLogs();
      loadAvailableTags();
      loadProjects();
    }
  }, [id, user]);

  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const loadProjects = async () => {
    if (!user?.id) return;

    try {
      setProjectsLoading(true);
      const response = await projectService.getUserProjects({
        user_id: user.id.toString()
      });
      if (response.success && response.projects) {
        const convertedProjects = response.projects.map(project => projectService.convertDatabaseProject(project));
        setProjects(convertedProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Load task blocks after task is loaded
  useEffect(() => {
    if (task && user?.id) {
      setContainerId(task.id.toString());
      // Always call loadBlocks, even with empty array to initialize the editor
      const blocksToLoad = task.blocks && task.blocks.length > 0 ? task.blocks : [];
      loadBlocks(blocksToLoad);
    }
  }, [task?.id, task?.blocks, user?.id, loadBlocks, setContainerId]);

  // Save operations are now handled by BlockManager

  // Task structure is now automatically updated by BlockManager through onContainerUpdate callback

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Helper function to convert content blocks to markdown text
  const blocksToMarkdown = (blocks: ContentBlock[]): string => {
    return blocks.map(block => block.text_value).join('\n');
  };

  const loadTask = async () => {
    if (!id || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await taskService.getTaskById(id);

      if (response.success && response.task) {
        console.log('🔧 Task loaded successfully:', {
          taskId: response.task.id,
          title: response.task.title,
          blocks: response.task.blocks,
          blocksLength: response.task.blocks?.length || 0,
          blocksType: typeof response.task.blocks
        });
        setTask(response.task);
        
        // Load tags for this task
        const tagsResponse = await tagService.getTagsForTask(response.task.id);
        if (tagsResponse.success && tagsResponse.tags) {
          setTaskTags(tagsResponse.tags);
        }
      } else {
        setError('Task not found');
      }
    } catch (err) {
      console.error('Error loading task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  // Task notes loading is now handled by useBlockManager hook

  const loadTimeLogs = async () => {
    if (!id || !user?.id) return;

    try {
      setTimeLogsLoading(true);
      
      // Get time logs for this specific task
      const logs = await TimeLogsService.getTimeLogsForActivity(user.id, 'task', id);
      
      // Sort by start time (newest first)
      const sortedLogs = TimeLogsService.sortTimeLogsByStartTime(logs, false);
      setTimeLogs(sortedLogs);
      
      // Calculate statistics
      const totalMinutes = logs.reduce((total, log) => total + log.duration_minutes, 0);
      const sessionCount = logs.length;
      const avgDurationMinutes = sessionCount > 0 ? Math.round(totalMinutes / sessionCount) : 0;
      
      setTimeStats({
        totalMinutes,
        totalHours: Math.round((totalMinutes / 60) * 100) / 100,
        sessionCount,
        avgDurationMinutes
      });
    } catch (err) {
      console.error('Error loading time logs:', err);
    } finally {
      setTimeLogsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !task || !user?.id) return;

    try {
      setIsAddingNote(true);
      
      // Create the note block using BlockService
      const noteBlock = await BlockService.createNoteBlock(user.id, newNote.trim());
      
      // Validate that we got a proper block_id
      if (!noteBlock.block_id || typeof noteBlock.block_id !== 'string') {
        throw new Error('Invalid block_id returned from block creation');
      }
      
      // Add the block ID to the task's blocks field
      const currentBlocks = task.blocks || [];
      const updatedBlocks = [...currentBlocks, noteBlock.block_id];
      
      // Update the task with the new blocks
      await taskService.updateTask({
        id: task.id.toString(),
        blocks: updatedBlocks
      });

      // Update the local task state with new blocks
      setTask(prev => prev ? { ...prev, blocks: updatedBlocks } : null);

      // Reload blocks through block manager
      await loadBlocks(updatedBlocks);

      // Clear the input
      setNewNote('');
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (note: TaskNote) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      // Remove the block ID from the task's blocks field
      const currentBlocks = task?.blocks || [];
      const updatedBlocks = currentBlocks.filter(blockId => blockId !== note.block_id);
      
      // Update the task with the updated blocks
      if (task) {
        await taskService.updateTask({
          id: task.id.toString(),
          blocks: updatedBlocks
        });
      }
      
      // Delete the block
      await BlockService.deleteBlock(note.block_id);
      
      // Update the local task state
      setTask(prev => prev ? { ...prev, blocks: updatedBlocks } : null);
      
      // Reload blocks through block manager
      await loadBlocks(updatedBlocks);
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  // Block operations are now handled by BlockManager through the BlockEditor

  const handleInlineEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValues({ ...editValues, [field]: value });
  };

  const handleInlineSave = async (field: string) => {
    if (!task) return;

    const value = editValues[field];
    if (value === undefined) return;

    console.log(`Saving field ${field} with value:`, value);

    // Validate required fields
    if (field === 'title' && (!value || !value.trim())) {
      setError('Title is required');
      return;
    }

    try {
      const updateData: Partial<UpdateTaskData> = {
        id: task.id.toString(),
        [field]: value
      };

      // Handle project_id specially - convert empty string to null
      if (field === 'project_id') {
        updateData.project_id = value ? Number(value) : null;
      }

      console.log('Sending update data:', updateData);

      const response = await taskService.updateTask(updateData as UpdateTaskData);
      
      if (response.success) {
        setEditingField(null);
        setEditValues({});
        await loadTask();
      } else {
        setError(response.message || `Failed to update ${field}`);
      }
    } catch (err: any) {
      console.error(`Error updating ${field}:`, err);
      setError(err.message || `Failed to update ${field}`);
    }
  };

  const handleInlineCancel = () => {
    setEditingField(null);
    setEditValues({});
  };

  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      // If the date string is already in YYYY-MM-DD format, return it as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Otherwise parse and format using local timezone to avoid date shifts
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleDelete = async () => {
    if (!task || !window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await taskService.deleteTask(task.id.toString());
      
      if (response.success) {
        navigate(-1); // Go back to previous page
      } else {
        setError(response.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  const handleStatusChange = async (newStatus: Task['status']) => {
    if (!task) return;

    try {
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        await loadTask();
      } else {
        setError(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleTagsChange = async (newTags: string[]) => {
    if (!task) return;

    try {
      const response = await taskService.updateTask({
        id: task.id.toString(),
        tag_names: newTags
      });

      if (response.success) {
        setTaskTags(newTags);
        await loadTask(); // Reload to ensure sync
      } else {
        setError(response.message || 'Failed to update tags');
      }
    } catch (err) {
      console.error('Error updating tags:', err);
      setError('Failed to update tags');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#007bff';
      case 'on_hold': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#666666';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date) < new Date();
  };

  // Timer functions
  const startTimer = () => {
    if (!task || !user?.id) return;
    
    const startTime = TimeLogsService.getCurrentTimestamp();
    setTimerStartTime(startTime);
    setIsTimerRunning(true);
    setTimerDuration(0);
    
    const interval = setInterval(() => {
      setTimerDuration(prev => prev + 1);
    }, 1000);
    
    setTimerInterval(interval);
  };

  const stopTimer = async () => {
    if (!task || !user?.id || !timerStartTime) return;
    
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    
    setIsTimerRunning(false);
    
    try {
      const endTime = TimeLogsService.getCurrentTimestamp();
      const durationMinutes = Math.round(timerDuration / 60);
      
      if (durationMinutes > 0) {
        await TimeLogsService.createTimeLog({
          user_id: user.id,
          activity_type: 'task',
          activity_id: task.id.toString(),
          activity_name: task.title,
          duration_minutes: durationMinutes,
          start_time: timerStartTime,
          end_time: endTime,
          description: 'Timer session',
          is_manual_entry: false
        });
        
        // Reload time logs
        await loadTimeLogs();
      }
    } catch (err) {
      console.error('Error saving timer session:', err);
      setError('Failed to save timer session');
    }
    
    setTimerStartTime(null);
    setTimerDuration(0);
  };

  const formatTimerDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Manual time entry functions
  const handleManualEntrySubmit = async () => {
    if (!task || !user?.id || !manualEntry.duration) return;
    
    try {
      const durationMinutes = parseInt(manualEntry.duration);
      if (durationMinutes <= 0) {
        setError('Duration must be greater than 0');
        return;
      }
      
      const now = new Date();
      const startTime = manualEntry.startTime ? new Date(manualEntry.startTime).toISOString() : now.toISOString();
      const endTime = manualEntry.endTime ? new Date(manualEntry.endTime).toISOString() : new Date(now.getTime() + durationMinutes * 60 * 1000).toISOString();
      
      await TimeLogsService.createTimeLog({
        user_id: user.id,
        activity_type: 'task',
        activity_id: task.id.toString(),
        activity_name: task.title,
        duration_minutes: durationMinutes,
        start_time: startTime,
        end_time: endTime,
        description: manualEntry.description || 'Manual entry',
        is_manual_entry: true
      });
      
      // Reset form and reload
      setManualEntry({ duration: '', description: '', startTime: '', endTime: '' });
      setShowManualEntry(false);
      await loadTimeLogs();
    } catch (err) {
      console.error('Error creating manual time entry:', err);
      setError('Failed to create time entry');
    }
  };

  const handleDeleteTimeLog = async (timeLogId: string) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await TimeLogsService.deleteTimeLog(timeLogId);
      await loadTimeLogs();
    } catch (err) {
      console.error('Error deleting time log:', err);
      setError('Failed to delete time entry');
    }
  };

  if (!user) {
    return (
      <div className="task-detail">
        <div className="task-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Task Details</h1>
        </div>
        <div className="auth-required">
          <p>Please sign in to view task details</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="task-detail">
        <div className="task-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Task Details</h1>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading task...</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="task-detail">
        <div className="task-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Task Details</h1>
        </div>
        <div className="error-state">
          <p>{error || 'Task not found'}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="task-detail">
      <div className="task-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ←
        </button>
        <div className="task-actions">
          <button 
            className="quick-actions-btn"
            onClick={() => setShowQuickActionsPopover(!showQuickActionsPopover)}
            title="Quick Actions"
          >
            ⋯
          </button>
        </div>
      </div>

      {/* Quick Actions Popover */}
      {showQuickActionsPopover && (
        <div className="quick-actions-popover-overlay" onClick={() => setShowQuickActionsPopover(false)}>
          <div className="quick-actions-popover" onClick={(e) => e.stopPropagation()}>
            <div className="popover-header">
              <h3>Quick Actions</h3>
              <button className="popover-close" onClick={() => setShowQuickActionsPopover(false)}>×</button>
            </div>
            <div className="popover-content">
              <div className="action-buttons">
                {task.status !== 'completed' && (
                  <button 
                    onClick={() => {
                      handleStatusChange('completed');
                      setShowQuickActionsPopover(false);
                    }}
                    className="action-btn complete-btn"
                  >
                    ✓ Mark Complete
                  </button>
                )}
                {task.status === 'completed' && (
                  <button 
                    onClick={() => {
                      handleStatusChange('todo');
                      setShowQuickActionsPopover(false);
                    }}
                    className="action-btn reopen-btn"
                  >
                    ↶ Reopen Task
                  </button>
                )}
                {task.status !== 'in_progress' && task.status !== 'completed' && (
                  <button 
                    onClick={() => {
                      handleStatusChange('in_progress');
                      setShowQuickActionsPopover(false);
                    }}
                    className="action-btn progress-btn"
                  >
                    🔄 Start Progress
                  </button>
                )}
                {task.status !== 'on_hold' && task.status !== 'completed' && (
                  <button 
                    onClick={() => {
                      handleStatusChange('on_hold');
                      setShowQuickActionsPopover(false);
                    }}
                    className="action-btn hold-btn"
                  >
                    ⏸️ Put on Hold
                  </button>
                )}
                <button 
                  onClick={() => {
                    handleDelete();
                    setShowQuickActionsPopover(false);
                  }}
                  className="action-btn delete-btn"
                >
                  🗑️ Delete Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="task-detail-content">
        <div className="task-info">
          {/* Task Title */}
          <div className="task-title-section">
            {editingField === 'title' ? (
              <div className="inline-edit-wrapper">
                <textarea
                  value={editValues.title || ''}
                  onChange={(e) => setEditValues({ ...editValues, title: e.target.value })}
                  className="inline-edit-textarea title-textarea"
                  rows={3}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleInlineSave('title');
                    }
                    if (e.key === 'Escape') handleInlineCancel();
                  }}
                />
                <div className="inline-edit-actions">
                  <button onClick={() => handleInlineSave('title')} className="save-inline-btn">✓</button>
                  <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                </div>
              </div>
            ) : (
              <h1 
                className="task-title-editable"
                onClick={() => handleInlineEdit('title', task.title)}
                title="Click to edit title"
              >
                {task.title}
              </h1>
            )}
          </div>
          
          {/* Project Section - Prominent */}
          <div className="task-project-section" style={{ marginBottom: '1rem', paddingLeft: '0.5rem' }}>
            {editingField === 'project_id' ? (
              <div className="inline-edit-wrapper" style={{ maxWidth: '300px' }}>
                <select
                  value={editValues.project_id || ''}
                  onChange={(e) => setEditValues({ ...editValues, project_id: e.target.value })}
                  className="inline-edit-select"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleInlineSave('project_id');
                    if (e.key === 'Escape') handleInlineCancel();
                  }}
                >
                  <option value="">No Project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <div className="inline-edit-actions">
                  <button onClick={() => handleInlineSave('project_id')} className="save-inline-btn">✓</button>
                  <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                </div>
              </div>
            ) : (
              <div 
                className="project-display" 
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  border: '1px solid transparent',
                  transition: 'all 0.2s ease',
                  fontSize: '0.875rem',
                  color: '#666666',
                  marginLeft: '-0.5rem'
                }}
              >
                <span 
                  style={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => handleInlineEdit('project_id', task.project_id?.toString() || '')}
                  title="Click to change project"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#333333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#666666';
                  }}
                >
                  Project:
                </span>
                {task.project_id ? (
                  <span 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${task.project_id}`);
                    }}
                    style={{ 
                      color: '#333333',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'text-decoration 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                      e.currentTarget.style.color = '#333333';
                    }}
                    title="Click to view project"
                  >
                    {projects.find(p => p.id === task.project_id?.toString())?.name || 'Unknown Project'}
                  </span>
                ) : (
                  <span style={{ 
                    color: '#999999',
                    fontStyle: 'italic'
                  }}>
                    None
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Tags Section - Always Visible */}
          <div className="task-tags-section">
            <TagSelector
              selectedTags={taskTags}
              onChange={handleTagsChange}
              availableTags={availableTags}
              placeholder="Add tags..."
            />
          </div>

          {/* Description Section - Collapsible */}
          <div className="task-description-section">
            <button 
              className="section-toggle"
              onClick={() => setShowDescriptionSection(!showDescriptionSection)}
            >
              <span className="toggle-text">/description</span>
              <span className="toggle-arrow">{showDescriptionSection ? '▼' : '▶'}</span>
            </button>
            
            <div className={`section-content ${showDescriptionSection ? 'expanded' : 'collapsed'}`}>
              {editingField === 'description' ? (
                <div className="inline-edit-wrapper">
                  <textarea
                    value={editValues.description || ''}
                    onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                    className="inline-edit-textarea"
                    rows={4}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') handleInlineCancel();
                    }}
                  />
                  <div className="inline-edit-actions">
                    <button onClick={() => handleInlineSave('description')} className="save-inline-btn">✓</button>
                    <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                  </div>
                </div>
              ) : (
                <p 
                  className="task-description-editable"
                  onClick={() => handleInlineEdit('description', task.description || '')}
                  title="Click to edit description"
                >
                  {task.description || 'Click to add description...'}
                </p>
              )}
            </div>
          </div>

          {/* Metadata Section - Collapsible */}
          <div className="task-metadata-section">
            <button 
              className="section-toggle"
              onClick={() => setShowMetadataSection(!showMetadataSection)}
            >
              <span className="toggle-text">/metadata</span>
              <span className="toggle-arrow">{showMetadataSection ? '▼' : '▶'}</span>
            </button>
            
            <div className={`section-content ${showMetadataSection ? 'expanded' : 'collapsed'}`}>
              <div className="metadata-grid">
              <div className="metadata-item">
                <label>Status</label>
                {editingField === 'status' ? (
                  <div className="inline-edit-wrapper">
                    <select
                      value={editValues.status || ''}
                      onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                      className="inline-edit-select"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineSave('status');
                        if (e.key === 'Escape') handleInlineCancel();
                      }}
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <div className="inline-edit-actions">
                      <button onClick={() => handleInlineSave('status')} className="save-inline-btn">✓</button>
                      <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                    </div>
                  </div>
                ) : (
                  <span 
                    className="metadata-value-editable"
                    onClick={() => handleInlineEdit('status', task.status)}
                    title="Click to edit status"
                  >
                    {task.status.replace('_', ' ')}
                  </span>
                )}
              </div>

              <div className="metadata-item">
                <label>Assigned Date</label>
                {editingField === 'assigned_date' ? (
                  <div className="inline-edit-wrapper">
                    <input
                      type="date"
                      value={editValues.assigned_date || ''}
                      onChange={(e) => setEditValues({ ...editValues, assigned_date: e.target.value })}
                      className="inline-edit-date"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineSave('assigned_date');
                        if (e.key === 'Escape') handleInlineCancel();
                      }}
                    />
                    <div className="inline-edit-actions">
                      <button onClick={() => handleInlineSave('assigned_date')} className="save-inline-btn">✓</button>
                      <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                    </div>
                  </div>
                ) : (
                  <span 
                    className="metadata-value-editable"
                    onClick={() => handleInlineEdit('assigned_date', formatDateForInput(task.assigned_date || null))}
                    title="Click to edit assigned date"
                  >
                    {task.assigned_date ? formatDate(task.assigned_date) : 'Click to set date...'}
                  </span>
                )}
              </div>

              <div className="metadata-item">
                <label>Due Date</label>
                {editingField === 'due_date' ? (
                  <div className="inline-edit-wrapper">
                    <input
                      type="date"
                      value={editValues.due_date || ''}
                      onChange={(e) => setEditValues({ ...editValues, due_date: e.target.value })}
                      className="inline-edit-date"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleInlineSave('due_date');
                        if (e.key === 'Escape') handleInlineCancel();
                      }}
                    />
                    <div className="inline-edit-actions">
                      <button onClick={() => handleInlineSave('due_date')} className="save-inline-btn">✓</button>
                      <button onClick={handleInlineCancel} className="cancel-inline-btn">✕</button>
                    </div>
                  </div>
                ) : (
                  <span 
                    className={`metadata-value-editable ${task.due_date && isOverdue(task) ? 'overdue' : ''}`}
                    onClick={() => handleInlineEdit('due_date', formatDateForInput(task.due_date || null))}
                    title="Click to edit due date"
                  >
                    {task.due_date ? (
                      <>
                        {formatDate(task.due_date)}
                        {isOverdue(task) && ' (Overdue)'}
                      </>
                    ) : (
                      'Click to set due date...'
                    )}
                  </span>
                )}
              </div>

              <div className="metadata-item">
                <label>Created</label>
                <span>{formatDateTime(task.created_at)}</span>
              </div>

              <div className="metadata-item">
                <label>Last Updated</label>
                <span>{formatDateTime(task.updated_at)}</span>
              </div>

              {task.completed_at && (
                <div className="metadata-item">
                  <label>Completed</label>
                  <span>{formatDateTime(task.completed_at)}</span>
                </div>
              )}

              </div>
            </div>
          </div>

          {/* Time Tracking Section - Collapsible */}
          <div className="task-time-tracking-section">
            <button 
              className="section-toggle"
              onClick={() => setShowTimeTrackingSection(!showTimeTrackingSection)}
            >
              <span className="toggle-text">/time</span>
              <span className="toggle-arrow">{showTimeTrackingSection ? '▼' : '▶'}</span>
            </button>
            
            <div className={`section-content ${showTimeTrackingSection ? 'expanded' : 'collapsed'}`}>
              {/* Time Statistics */}
              <div className="time-stats">
                <div className="time-stat-item">
                  <span className="time-stat-label">Total Time</span>
                  <span className="time-stat-value">{TimeLogsService.formatDuration(timeStats.totalMinutes)}</span>
                </div>
                <div className="time-stat-item">
                  <span className="time-stat-label">Sessions</span>
                  <span className="time-stat-value">{timeStats.sessionCount}</span>
                </div>
                <div className="time-stat-item">
                  <span className="time-stat-label">Avg Duration</span>
                  <span className="time-stat-value">{TimeLogsService.formatDuration(timeStats.avgDurationMinutes)}</span>
                </div>
              </div>

              {/* Timer Controls */}
              <div className="timer-controls">
                <div className="timer-display">
                  {isTimerRunning ? (
                    <span className="timer-running">{formatTimerDuration(timerDuration)}</span>
                  ) : (
                    <span className="timer-stopped">00:00</span>
                  )}
                </div>
                <div className="timer-buttons">
                  {!isTimerRunning ? (
                    <button 
                      onClick={startTimer}
                      className="timer-btn start-btn"
                      disabled={!task}
                    >
                      ▶ Start Timer
                    </button>
                  ) : (
                    <button 
                      onClick={stopTimer}
                      className="timer-btn stop-btn"
                    >
                      ⏹ Stop Timer
                    </button>
                  )}
                  <button 
                    onClick={() => setShowManualEntry(!showManualEntry)}
                    className="timer-btn manual-btn"
                  >
                    + Manual Entry
                  </button>
                </div>
              </div>

              {/* Manual Entry Form */}
              {showManualEntry && (
                <div className="manual-entry-form">
                  <div className="form-row">
                    <input
                      type="number"
                      placeholder="Duration (minutes)"
                      value={manualEntry.duration}
                      onChange={(e) => setManualEntry({ ...manualEntry, duration: e.target.value })}
                      className="manual-input"
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={manualEntry.description}
                      onChange={(e) => setManualEntry({ ...manualEntry, description: e.target.value })}
                      className="manual-input"
                    />
                  </div>
                  <div className="form-row">
                    <input
                      type="datetime-local"
                      placeholder="Start time (optional)"
                      value={manualEntry.startTime}
                      onChange={(e) => setManualEntry({ ...manualEntry, startTime: e.target.value })}
                      className="manual-input"
                    />
                    <input
                      type="datetime-local"
                      placeholder="End time (optional)"
                      value={manualEntry.endTime}
                      onChange={(e) => setManualEntry({ ...manualEntry, endTime: e.target.value })}
                      className="manual-input"
                    />
                  </div>
                  <div className="form-actions">
                    <button 
                      onClick={handleManualEntrySubmit}
                      disabled={!manualEntry.duration}
                      className="submit-btn"
                    >
                      Add Entry
                    </button>
                    <button 
                      onClick={() => setShowManualEntry(false)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Time Logs List */}
              {timeLogsLoading ? (
                <div className="time-logs-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading time logs...</p>
                </div>
              ) : timeLogs.length > 0 ? (
                <div className="time-logs-list">
                  <h4>Time Entries</h4>
                  {timeLogs.map((log) => (
                    <div key={log.id} className="time-log-item">
                      <div className="time-log-header">
                        <span className="time-log-duration">{TimeLogsService.formatDuration(log.duration_minutes)}</span>
                        <span className="time-log-date">{TimeLogsService.formatDate(log.start_time)}</span>
                        <button 
                          onClick={() => handleDeleteTimeLog(log.timelog_id)}
                          className="delete-time-log-btn"
                          title="Delete time entry"
                        >
                          ×
                        </button>
                      </div>
                      <div className="time-log-details">
                        <span className="time-log-time">
                          {TimeLogsService.formatTime(log.start_time)} - {TimeLogsService.formatTime(log.end_time)}
                        </span>
                        {log.description && (
                          <span className="time-log-description">{log.description}</span>
                        )}
                        <span className="time-log-type">
                          {log.is_manual_entry ? 'Manual' : 'Timer'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-time-logs">
                  <p>No time entries yet. Start a timer or add a manual entry to track time spent on this task.</p>
                </div>
              )}
            </div>
          </div>

      
        </div>

        {/* Notes Display - Block Editor */}
        {blocksLoading ? (
          <div className="notes-loading">
            <div className="loading-spinner"></div>
            <p>Loading notes...</p>
          </div>
        ) : (
          <div className="task-notes-editor">
            <BlockEditor
              blockManager={blockManager}
              blocks={contentBlocks}
              placeholder={`Add notes about "${task?.title || 'this task'}"...`}
              onSyncStatusChange={(status) => {}} // Sync status is handled by hook
            />
          </div>
        )}
      </div>

      {/* Add New Note Form - Fixed to Bottom */}
      <div className="add-note-form-fixed">
        <div className="chat-input-container">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a new thought or note about this task..."
            className="chat-input"
            rows={1}
            disabled={isAddingNote}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddNote();
              }
            }}
          />
          <button
            onClick={handleAddNote}
            disabled={!newNote.trim() || isAddingNote}
            className="chat-send-btn"
            title="Send note"
          >
            {isAddingNote ? '...' : '→'}
          </button>
        </div>
      </div>

      {/* Sync Status Icon */}
      <div className={`task-sync-status-icon ${syncStatus}`}>
        {syncStatus === 'syncing' && <div className="task-sync-spinner">⟳</div>}
        {syncStatus === 'synced' && <div className="task-sync-check">✓</div>}
        {syncStatus === 'error' && <div className="task-sync-error">⚠</div>}
      </div>
    </div>
  );
};

export default TaskDetail;
